import React, { useEffect, useState } from 'react';
import { Accordion, Button, Card, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pluralize from 'pluralize';

import { Collaboration } from '@models/Collaboration';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConfirmModal } from '@components/Base';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import {
  CollaborationsFetchPayload,
  collaborationCreate,
  CollaborationCreatePayload,
  collaborationUpdate,
  CollaborationUpdatePayload,
  collaborationDelete,
  CollaborationDeletePayload,
  StateUpdatePayload,
  stateUpdate,
} from './action';
import { selectFeatureCollaboration } from './selector';
import { CollaborationFormModal, CollaborationFormValues } from './collaboration-form-modal';
import { CollaborationListView } from './collaboration-list-view';

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<CollaborationsFetchPayload, 'collaboratableType' | 'collaboratableId'> & {
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
};

const CollaborationCardBody = ({
  collaboratableType,
  collaboratableId,
  canCreate = false,
  canEdit = false,
  canDelete = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isSuccess, pagination } = useSelector(selectFeatureCollaboration);
  const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
  const [showCollaborationFormModal, setShowCollaborationFormModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleEdit = (collaboration: Collaboration) => {
    setSelectedCollaboration(collaboration);
    setShowCollaborationFormModal(true);
  };

  const handleDelete = (collaboration: Collaboration) => {
    setSelectedCollaboration(collaboration);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setSelectedCollaboration(null);
    setShowCollaborationFormModal(false);
    const payload: StateUpdatePayload = { isSuccess: false, serverErrors: {} };
    dispatch(stateUpdate(payload));
  };

  useEffect(() => {
    if (isSuccess && showCollaborationFormModal) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              {pluralize("Collaboration", 2)} ({pagination?.total ?? 0})
            </Toggle>
            {canCreate && (
              <Button onClick={() => setShowCollaborationFormModal(true)} variant="outline-primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {t('common:add')}
              </Button>
            )}
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <CollaborationListView {...{
              collaboratableType,
              collaboratableId,
              onEdit: canEdit ? handleEdit : undefined,
              onDelete: canDelete ? handleDelete : undefined
            }} />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <CollaborationFormModal
        show={showCollaborationFormModal}
        collaboration={selectedCollaboration}
        onHide={handleClose}
        onSubmit={(values: CollaborationFormValues) => {
          if (selectedCollaboration) {
            const payload: CollaborationUpdatePayload = { id: selectedCollaboration.id, ...values };
            dispatch(collaborationUpdate(payload));
          } else {
            const payload: CollaborationCreatePayload = { collaboratableType, collaboratableId, ...values };
            dispatch(collaborationCreate(payload));
          }
        }}
      />

      {canDelete && (
        <ConfirmModal
          show={showConfirmModal}
          onHide={() => {
            setShowConfirmModal(false);
            setSelectedCollaboration(null);
          }}
          onConfirm={() => {
            if (selectedCollaboration) {
              const payload: CollaborationDeletePayload = { id: selectedCollaboration.id };
              dispatch(collaborationDelete(payload));
              setShowConfirmModal(false);
              setSelectedCollaboration(null);
            }
          }}
        />
      )}
    </>
  );
};

export { CollaborationCardBody };
